import { useCreditsStore } from '../../store/credits';
import { useCredits } from '../../hooks/useCredits';
import { useSettingsStore } from '../../store/settings';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
    </div>
  );
}

export function CreditsTab() {
  // Use store directly for reactive updates
  const { credits, monthly, maxCredits, used, isLoading, error } = useCreditsStore();
  const { setActiveSettingsTab } = useSettingsStore();
  // Use hook for refetch function
  const { refetch } = useCredits();

  const total = maxCredits;
  const remaining = credits;
  const usagePercent = total > 0 ? ((total - credits) / total) * 100 : 0;

  if (isLoading && credits === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Credit Balance</h2>
          <p className="text-sm text-gray-500">Manage your AI credits and top up.</p>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Credit Balance</h2>
          <p className="text-sm text-gray-500">Manage your AI credits and top up.</p>
        </div>
        <div className="border border-red-200 rounded-xl p-6 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 text-sm font-medium text-red-600 hover:text-red-800"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Credit Balance</h2>
        <p className="text-sm text-gray-500">Manage your AI credits and top up.</p>
      </div>

      <div className="border border-gray-200 rounded-xl p-6 space-y-6">
        {/* Used this period */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Used this period</span>
          <span className="text-sm font-medium text-gray-900">{used}</span>
        </div>

        {/* Main balance display */}
        <div className="text-center py-4">
          <div className="text-5xl font-bold text-gray-900">{remaining}</div>
          <div className="text-sm text-gray-500 mt-1">of {total} available</div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, usagePercent)}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-gray-500">Usage</div>
        </div>

        {/* Monthly / Bonus breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-xl p-4 text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Monthly</div>
            <div className="text-2xl font-semibold text-gray-900">{monthly}</div>
          </div>
          <div className="border border-gray-200 rounded-xl p-4 text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Bonus</div>
            <div className="text-2xl font-semibold text-gray-900">0</div>
          </div>
        </div>

        {/* Upgrade Plan Button */}
        <button
          onClick={() => setActiveSettingsTab('subscription')}
          className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-700 flex items-center justify-center gap-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
          </svg>
          Upgrade Plan
        </button>

        {/* Reset date - placeholder for future */}
        <div className="text-center text-sm text-gray-500">
          Credits are refreshed monthly
        </div>
      </div>
    </div>
  );
}
